import React from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";

import { useGetOrganization } from "~/api/organizations/organizations";
import { cn } from "~/lib/utils";
import { Progress } from "./progress";

interface FreeTrialProgressBarProps {
  className?: string;
}

const DEFAULT_FREE_TRIAL_LIMIT = 1000;
const REFETCH_INTERVAL_MS = 1 * 60 * 1000;

export const FreeTrialProgressBar: React.FC<FreeTrialProgressBarProps> = ({
  className,
}) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organizationInfo } = useGetOrganization(
    organizationId as string,
    {
      query: {
        enabled: !!organizationId,
        refetchInterval: REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: true,
      },
    }
  );

  const pagesUsed = Number(organizationInfo?.pagesUsedInThisBillingPeriod) || 0;
  const freeTrialLimit =
    Number(organizationInfo?.freeTrialLimit) || DEFAULT_FREE_TRIAL_LIMIT;

  const usagePercentage = Math.min((pagesUsed / freeTrialLimit) * 100, 100);

  const getProgressColor = (percentage: number) => {
    if (percentage < 70) return "bg-green-600";
    if (percentage < 90) return "bg-yellow-600";
    return "bg-red-600";
  };

  const progressColor = getProgressColor(usagePercentage);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
        <FormattedMessage
          id="freeTrialProgress.label"
          defaultMessage="Free Trial"
        />
      </span>
      <Progress
        value={usagePercentage}
        className="h-2 w-20"
        indicatorClassName={progressColor}
      />
      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
        {pagesUsed.toLocaleString()} / {freeTrialLimit.toLocaleString()}
      </span>
    </div>
  );
};
